import { useState } from "react";
import { RequirementRow } from "./requirements/RequirementRow";
import { RequirementsProgress, CategoryFilter } from "./requirements/RequirementsToolbar";
import { useRequirements } from "@/hooks/useRequirements";
import { ListChecks, RefreshCw } from "lucide-react";

const generateLabel = (busy, count) => {
  if (busy) return "Reading spec…";
  return count ? "Regenerate" : "Summarize fabricator requirements";
};

export function RequirementsSummary({ projectId, canEdit }) {
  const req = useRequirements(projectId);
  const { data, reqs, busy, generate } = req;
  const [cat, setCat] = useState("");
  const [onlyOpen, setOnlyOpen] = useState(false);
  const counts = data?.scope_counts || { included: 0, recommended: 0, confirmed: 0, needs_review: 0, excluded: 0 };
  const struck = counts.excluded;
  const shown = reqs.filter((r) => (!cat || r.category === cat) && (!onlyOpen || r.scope?.state === "needs_review"));
  return (
    <div className="sc-card p-6 mb-4" data-testid="report-section-requirements">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="label-cap flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5 text-amber-400" /> What the specification requires of the fabricator {reqs.length ? `(${reqs.length})` : ""}</div>
          {data?.headline && <p className="text-sm text-slate-200 mt-2 max-w-3xl" data-testid="requirements-headline">{data.headline}</p>}
          {data?.generated_at && <div className="text-[11px] text-slate-500 mt-1 font-mono">Generated {new Date(data.generated_at).toLocaleString()} · {data.non_standard_count} exceed AISC/AWS/RCSC defaults ▲{struck > 0 && <span className="text-red-400"> · {struck} excluded</span>}</div>}
        </div>
        {canEdit && (
          <button onClick={() => generate(!!reqs.length)} disabled={busy} className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-semibold px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="generate-requirements-button">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {generateLabel(busy, reqs.length)}
          </button>
        )}
      </div>
      {!reqs.length && !busy && <p className="text-xs text-slate-500">Not generated yet. One click reads the Division 05 sections and lists every obligation on the fabricator — materials, welding/NDT, bolting, coatings, submittals, QA, delivery — each with its section and page.</p>}
      {reqs.length > 0 && (
        <>
          <RequirementsProgress reqs={reqs} counts={counts} />
          <CategoryFilter categories={data.categories} reqs={reqs} cat={cat} setCat={setCat} onlyOpen={onlyOpen} setOnlyOpen={setOnlyOpen} />
          <div className="divide-y divide-slate-800/60">
            {shown.map((r) => <RequirementRow key={r.id} r={r} cat={cat} canEdit={canEdit} toggle={req.toggle} override={req.override} reset={req.reset} noteFor={req.noteFor} setNoteFor={req.setNoteFor} note={req.note} setNote={req.setNote} />)}
          </div>
        </>
      )}
    </div>
  );
}
